import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  icon?: IconName
  /** A danger modal ignores a backdrop click: destructive acts need a decision. */
  tone?: 'default' | 'danger'
  footer?: ReactNode
  children: ReactNode
  width?: number
}

export function Modal({ open, onClose, title, icon, tone = 'default', footer, children, width }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key !== 'Tab') return
      const focusable = ref.current?.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])',
      )
      if (!focusable?.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    const previouslyFocused = document.activeElement as HTMLElement | null
    ref.current?.querySelector<HTMLElement>('button,input,a')?.focus()
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
      previouslyFocused?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="modal-root" role="presentation">
      <div className="scrim" onClick={tone === 'danger' ? undefined : onClose} />
      <div
        ref={ref}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={width ? { inlineSize: width } : undefined}
      >
        <div className={cx('modal-h', 'row')} style={{ gap: 10 }}>
          {icon && (
            <span
              className="glyph"
              style={{
                inlineSize: 34,
                blockSize: 34,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--r-md)',
                background: tone === 'danger' ? 'var(--c-danger-soft)' : 'var(--c-accent-soft)',
                color: tone === 'danger' ? 'var(--c-danger)' : 'var(--c-accent)',
              }}
            >
              <Icon name={icon} />
            </span>
          )}
          <div className="t-h3">{title}</div>
        </div>
        <div className="modal-b">{children}</div>
        {footer && <div className="modal-f">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

/** The phone presentation of a modal: a bottom sheet, as 19-mobile draws it. */
export function Sheet({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  if (!open) return null
  return createPortal(
    <div className="sheet-root" role="presentation">
      <div className="scrim" onClick={onClose} />
      <div className="sheet-panel" role="dialog" aria-modal="true">
        <div className="sheet-grab" />
        {children}
      </div>
    </div>,
    document.body,
  )
}
